import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CenteredScrollPage } from '@/components/shared/CenteredScrollPage';
import { useSignupComplete } from '@/hooks/useAuthQueries';
import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const COMPLETE_ATTEMPTS = 5;
const COMPLETE_RETRY_MS = 2000;

export default function SignupComplete() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const completeSignup = useSignupComplete();
  const [state, setState] = useState<'completing' | 'failed'>('completing');
  const started = useRef(false);

  const completeWithRetries = async () => {
    if (!sessionId) {
      setState('failed');
      return;
    }
    setState('completing');
    for (let attempt = 0; attempt < COMPLETE_ATTEMPTS; attempt++) {
      try {
        await completeSignup.mutateAsync(sessionId);
        navigate('/explore', { replace: true });
        return;
      } catch {
        // fall through to retry
      }
      await new Promise((resolve) => setTimeout(resolve, COMPLETE_RETRY_MS));
    }
    setState('failed');
  };

  useEffect(() => {
    if (!started.current) {
      started.current = true;
      void completeWithRetries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CenteredScrollPage>
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle>Finishing your signup</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {state === 'completing' ? (
            <p data-testid="signup-complete-pending" className="flex items-center text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Confirming your payment and creating your account...
            </p>
          ) : (
            <div className="space-y-2">
              <p data-testid="signup-complete-failed" className="text-sm text-destructive">
                {sessionId
                  ? "We couldn't confirm your signup yet. If you completed payment, it may take a moment to register."
                  : 'Missing checkout reference — please sign up again.'}
              </p>
              {sessionId && (
                <Button data-testid="signup-complete-retry" onClick={() => void completeWithRetries()}>
                  Check again
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </CenteredScrollPage>
  );
}
